import { useEffect, useCallback } from 'react';
import { useOnboardingFlow } from './useOnboardingFlow';

interface UseKeyboardNavigationProps {
  onNext?: () => void;
  isEnabled?: boolean;
}

export const useKeyboardNavigation = ({
  onNext,
  isEnabled = true,
}: UseKeyboardNavigationProps = {}) => {
  const { canProceed, currentStep } = useOnboardingFlow();

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    // Only handle Enter key
    if (event.key !== 'Enter') return;
    
    // Don't handle if keyboard navigation is disabled
    if (!isEnabled) return;
    
    // Don't handle if we're in an input field that might need Enter for its own purposes
    const target = event.target as HTMLElement;
    if (target?.tagName === 'TEXTAREA') return;
    
    // Don't handle Enter key navigation on the "Your Courses" page (step 5)
    if (currentStep === 5) return;
    
    // Only allow Enter when Next button would be enabled
    // Button logic: isNextDisabled={!canProceed() && currentStep !== 6}
    // So button is enabled when: canProceed() OR currentStep === 6
    const shouldAllowNavigation = canProceed() || currentStep === 6;
    if (!shouldAllowNavigation) return;
    
    // Prevent default behavior to avoid form submission
    event.preventDefault();
    
    // Call the provided onNext function or the default goNext
    if (onNext) {
      onNext();
    }
  }, [canProceed, currentStep, isEnabled, onNext]);

  useEffect(() => {
    // Only add event listener if enabled
    if (!isEnabled) return;

    document.addEventListener('keydown', handleKeyPress);
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress, isEnabled]);

  return {
    canUseKeyboard: isEnabled && (canProceed() || currentStep === 6),
  };
};